export const SPEC_THESIS = {
  tag: "SYSTEM MANIFEST // 04",
  status: "23 CAPABILITIES · 1 PIPELINE",
  headline: "The Full Infrastructure Specifications",
  insight: "Not one prompt: 23 integrated capabilities power every conversational turn.",
  subhead:
    "Each layer below is a specialized module in the infrastructure stack. Select a layer, then inspect any capability.",
};

export const SPEC_LAYERS = [
  {
    id: "orchestration",
    code: "L-01",
    name: "Orchestration",
    tagline: "Route, brand, gate",
    moduleIds: ["01", "02", "03", "04"],
  },
  {
    id: "commerce",
    code: "L-02",
    name: "Commerce",
    tagline: "Price, comply, qualify",
    moduleIds: ["05", "06", "07"],
  },
  {
    id: "scheduling",
    code: "L-03",
    name: "Scheduling",
    tagline: "Time, crew, book",
    moduleIds: ["08", "09", "10", "11", "12"],
  },
  {
    id: "protection",
    code: "L-04",
    name: "Protection",
    tagline: "Object, emergency, media",
    moduleIds: ["13", "14", "15", "16"],
  },
  {
    id: "integration",
    code: "L-05",
    name: "Integration",
    tagline: "CRM, quotes, state",
    moduleIds: ["17", "18", "19", "20"],
  },
  {
    id: "control",
    code: "L-06",
    name: "Control",
    tagline: "Hours, sync, dashboard",
    moduleIds: ["21", "22", "23"],
  },
];

export const SPECIFICATIONS = [
  {
    id: "01",
    title: "Multi-Intent Routing Orchestrator",
    subtitle: "01. Intent Router",
    description:
      "Detects multiple intents in a single inbound message. If a lead asks about services and prices simultaneously, it handles both intents in parallel and merges the responses into a single coherent turn.",
  },
  {
    id: "02",
    title: "Company Persona & Identity Engine",
    subtitle: "02. Brand & Identity Specialist",
    description:
      "Hard-coded compliance with the client's trading name, founding history, physical office policies, and brand voice. Strictly handles queries like \"Are you a bot?\" using pre-configured database facts with zero fiction.",
  },
  {
    id: "03",
    title: "Out-of-Scope Specialist & Auto-Decline",
    subtitle: "03. Service Catalog Gatekeeper",
    description:
      "Instantly validates requests against the allowed service catalog. Automatically executes a hard decline on out-of-scope requests (e.g., solar, asbestos) and gates the scheduling engine from continuing on rejected leads.",
  },
  {
    id: "04",
    title: "Geo-Validator & Radius Proximity Lock",
    subtitle: "04. Google Geocoding Validator",
    description:
      "Verifies complete addresses via Google Geocoding API. Uses the Haversine formula to compute exact distance from the client's office, executing a graceful closure if the lead is outside the maximum travel radius.",
  },
  {
    id: "05",
    title: "Ballpark Accountant & Repetition Ladder",
    subtitle: "05. Metered Pricing Accountant",
    description:
      "Deploys regional tax structures (GST/Sales Tax) and ballpark metrics. Uses a repetition ladder protocol to handle persistent pricing loops (Full answer → Short summary → Pivot to booking) and suppresses pricing during active emergency turns.",
  },
  {
    id: "06",
    title: "Contractor License & Compliance Verification",
    subtitle: "06. Legal & Compliance Counsel",
    description:
      "Serves dynamic company credentials (ABN/EIN, contractor licenses, public liability insurance, and warranty tiers) without copy-pasting the same raw text blocks on repeated questions.",
  },
  {
    id: "07",
    title: "15-Point Asynchronous Intake Protocol",
    subtitle: "07. Asynchronous Interviewer",
    description:
      "Conducts a human-like interview script (up to 15 data points). Extracts messy data from unified text blocks, tracks intake completeness, and handles objections regarding why data is required.",
  },
  {
    id: "08",
    title: "The Clock: Temporal Parsing Engine",
    subtitle: "08. Natural Language Time Parser",
    description:
      "Translates conversational time markers (\"tomorrow at 9\", \"next Thursday afternoon\") into clean ISO timestamps, adjusting instantly to the local IANA timezone of the target business asset.",
  },
  {
    id: "09",
    title: "Team Lead: Resource & Crew Routing",
    subtitle: "09. Resource Routing Manager",
    description:
      "Evaluates appointment types (On-site, Phone Consult, Emergency) and maps them to specific personnel or crew profiles (Crew Alpha/Beta) based on live availability states.",
  },
  {
    id: "10",
    title: "Stylist: Tone & Channel Formatter",
    subtitle: "10. Tone & Formatting Polish",
    description:
      "Controls the emoji budget, applies plain-text formatting scrubs for GoHighLevel pipelines, and triggers dialect toggles (such as localized Aussie slang) based on deployment settings.",
  },
  {
    id: "11",
    title: "Synthesizer: Unified Reply Engine",
    subtitle: "11. Response Composer",
    description:
      "Blends specialized processing steps into a single, clean conversational turn. Eliminates system-log leaks, suppresses duplicates, and applies an empathy-bridge marker before any booking CTA.",
  },
  {
    id: "12",
    title: "Live Calendar Sync & API Executor",
    subtitle: "12. Calendar Executor",
    description:
      "Executes direct API calls to write, reschedule, or cancel bookings natively within GoHighLevel or Google Calendar. Holds a strict anti-hallucination guard: it never says \"You're booked\" unless the server returns a successful execution flag.",
  },
  {
    id: "13",
    title: "Competitor & Price Objection Strategist",
    subtitle: "13. Sales Objections Strategist",
    description:
      "Handles soft-closing sequences on pricing objections (\"too expensive\") or competitor comparisons. Automatically disengages if the underlying lead state has been marked as disqualified.",
  },
  {
    id: "14",
    title: "Sentinel: Emergency & Hard Disqualification Gate",
    subtitle: "14. Emergency Sentinel",
    description:
      "Identifies immediate structural crises using semantic urgency triggers. Instantly deploys a 4-part emergency matrix (Safety action → On-call route → Address lock → Emergency fee notification) while separating chronic issues from active dispatch alerts.",
  },
  {
    id: "15",
    title: "Visual Auditor & Photo Gating Service",
    subtitle: "15. Media Auditor",
    description:
      "Provides structured instructions for lead image submissions (leaks, damage). Validates attachment URLs, tracks submission channels, and acts as a hard gate for specific appointment tiers that require photographic proof prior to scheduling.",
  },
  {
    id: "16",
    title: "Weather Sentry: Meteorological Guardrails",
    subtitle: "16. Local Weather API Integration",
    description:
      "Pulls real-time coordinate data via Open-Meteo API. Restricts outdoor site scheduling during heavy rain alerts or post-sunset daylight limits, explaining the safety parameters to the user and offering alternative slots.",
  },
  {
    id: "17",
    title: "CRM History Retriever & Known-User Bypass",
    subtitle: "17. Historical CRM Retriever",
    description:
      "Matches incoming phone numbers or emails against the database. Automatically pre-fills historical assets, bypasses cold qualification scripts for returning customers, and initiates verbal warranty recognition protocols.",
  },
  {
    id: "18",
    title: "Quote Logic Intersector & Dynamic Surcharges",
    subtitle: "18. Dynamic Slot Intersector",
    description:
      "Evaluates the specific calendar slot selected by the user to inject context-aware pricing tiers, such as public holiday surcharges, standard callout fees, or automated fee waivers.",
  },
  {
    id: "19",
    title: "Funnel Tracking & State Machines",
    subtitle: "19. Core Pipeline State Tracking",
    description:
      "Tracks every conversation through exact states (in_progress → booking_stage → meeting_booked → handoff_requested → disqualified) for clean analytical exports to the agency dashboard.",
  },
  {
    id: "20",
    title: "Out-of-Bounds & Spam Filtering",
    subtitle: "20. B2B Spam & Inbound Suppression",
    description:
      "Automatically isolates non-revenue traffic, blocking abstract queries or incoming B2B marketing pitches (SEO/advertising spam) without triggering a re-open loop inside the active sales funnel.",
  },
  {
    id: "21",
    title: "Digital Brain Schedule Gate & Timezone Overrides",
    subtitle: "21. Operational Availability Windowing",
    description:
      "Controls exactly when the AI Employee responds to the public. Includes date overrides for holidays and special closures, capturing messages silently during dark hours for delayed queuing.",
  },
  {
    id: "22",
    title: "Structured Executive CRM Sync",
    subtitle: "22. Deep CRM Synchronization",
    description:
      "Writes a comprehensive technical summary directly into GoHighLevel Opportunity Comments upon handoff, logging full contact details, the qualification matrix, and a deep-link back to the FractoLabs control console.",
  },
  {
    id: "23",
    title: "No-Code Dashboard Architecture",
    subtitle: "23. Zero-Code Control Core",
    description:
      "Full operational configuration handled natively within the React/Vite dashboard interface. Update knowledge bases, calendars, and script behaviors on the fly with no backend access required.",
  },
];
